import type { AxiosProgressEvent } from 'axios'

import { defaultApiHost } from '@src/helpers'

/**
 * Handles API response
 */
export function handleResponse(response: Response) {
  switch (response.status) {
    case 201:
    case 200:
      return response.json()
    case 204:
      return null
    case 400:
      throw new Error('Bad request')
    case 401:
    case 403:
      throw new Error('Unauthorized')
    case 404:
      throw new Error('Resource not found')
    default:
      throw new Error('Request failed with status code ' + response.status)
  }
}

const loadAxios = async () => {
  const axiosModule = await import('axios')
  return axiosModule.default
}

interface CreateUploadSignatureProps {
  payload: {
    filename: string
    mime_type: string
    actor_id: string
  }
  apiHost?: string
}

export interface CreateUploadSignatureResponse {
  acl: string
  algorithm: string
  credential: string
  date: string
  key: string
  policy: string
  post_url: string
  signature: string
}

interface UploadFileProps {
  uploadSignature: CreateUploadSignatureResponse
  file: File
  onUploadProgress?: (progressEvent: AxiosProgressEvent) => void
}

/**
 * Creates new upload signature for uploading of attachments to S3
 */
export const createUploadSignature = async ({
  apiHost = defaultApiHost,
  payload,
}: CreateUploadSignatureProps): Promise<CreateUploadSignatureResponse> => {
  const response = await fetch(`${apiHost}/api/one_to_one/upload_signatures`, {
    method: 'POST',
    body: JSON.stringify(payload),
    headers: {
      'Content-type': 'application/json; charset=UTF-8',
    },
  })
  return handleResponse(response) as unknown as CreateUploadSignatureResponse
}

/**
 * Uploads file to S3
 */
export const uploadFile = async ({
  file,
  uploadSignature,
  onUploadProgress,
}: UploadFileProps) => {
  const axios = await loadAxios()

  const formData = new FormData()
  formData.append('Content-Type', file.type)
  formData.append('key', uploadSignature.key)
  formData.append('acl', uploadSignature.acl)
  formData.append('policy', uploadSignature.policy)
  formData.append('x-amz-credential', uploadSignature.credential)
  formData.append('x-amz-algorithm', uploadSignature.algorithm)
  formData.append('x-amz-date', uploadSignature.date)
  formData.append('x-amz-signature', uploadSignature.signature)
  formData.append('file', file)

  await axios.post(uploadSignature.post_url, formData, {
    onUploadProgress,
  })
}
